import React, { useCallback, useEffect, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, List, ChevronLeft, AlertTriangle, Lock, Clock } from 'lucide-react';
import { EpisodeList } from './EpisodeList';
interface EpisodeNavScreenProps {
  onPlay: () => void;
  onBack: () => void;
  onNextEpisode: () => void;
}
export function EpisodeNavScreen({
  onPlay,
  onBack,
  onNextEpisode
}: EpisodeNavScreenProps) {
  const [isListOpen, setIsListOpen] = useState(false);
  const [hasWatched, setHasWatched] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeLeft, setTimeLeft] = useState<{
    h: number;
    m: number;
    s: number;
  } | null>(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  // Countdown to today at 23:00 local time
  useEffect(() => {
    const getUnlockTime = () => {
      const now = new Date();
      const unlock = new Date(now);
      unlock.setHours(23, 0, 0, 0);
      // If 23:00 already passed today, it's unlocked
      if (now >= unlock) return null;
      return unlock;
    };
    const updateCountdown = () => {
      const unlockTime = getUnlockTime();
      if (!unlockTime) {
        setIsUnlocked(true);
        setTimeLeft(null);
        return;
      }
      const now = new Date();
      const diff = unlockTime.getTime() - now.getTime();
      if (diff <= 0) {
        setIsUnlocked(true);
        setTimeLeft(null);
        return;
      }
      const h = Math.floor(diff / (1000 * 60 * 60));
      const m = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
      const s = Math.floor(diff % (1000 * 60) / 1000);
      setTimeLeft({
        h,
        m,
        s
      });
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);
  const handlePlayFullscreen = useCallback(async () => {
    const video = videoRef.current;
    const container = videoContainerRef.current;
    if (!video || !container) return;
    setIsPlaying(true);
    try {
      // Try fullscreen on the container first (better styling control)
      if (container.requestFullscreen) {
        await container.requestFullscreen();
      } else if ((container as any).webkitRequestFullscreen) {
        await (container as any).webkitRequestFullscreen();
      }
      await video.play();
    } catch (err) {
      // If fullscreen fails, just play inline
      await video.play();
    }
  }, []);
  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if ((document as any).webkitFullscreenElement) {
      ;
      (document as any).webkitExitFullscreen();
    }
  }, []);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleEnded = () => {
      setIsPlaying(false);
      setHasWatched(true);
      exitFullscreen();
    };
    const handleFullscreenChange = () => {
      // If user manually exits fullscreen while video is playing
      if (!document.fullscreenElement && !(document as any).webkitFullscreenElement) {
        if (video && !video.ended && !video.paused) {
          // Video still playing — let it continue inline, mark as watched when done
        }
        if (video && video.ended) {
          setIsPlaying(false);
          setHasWatched(true);
        }
      }
    };
    video.addEventListener('ended', handleEnded);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      video.removeEventListener('ended', handleEnded);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, [exitFullscreen]);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    },
    exit: {
      opacity: 0,
      y: -50,
      transition: {
        duration: 0.5
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };
  return <motion.div className="min-h-screen w-full bg-[var(--bg-secondary)] flex flex-col relative overflow-hidden" variants={containerVariants} initial="hidden" animate="visible" exit="exit" data-id="element-80">
      {/* Background Texture */}
      <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] pointer-events-none mix-blend-overlay" data-id="element-81" />

      {/* Hidden fullscreen video container */}
      <div ref={videoContainerRef} className={`${isPlaying && !hasWatched ? 'block' : 'hidden'} fixed inset-0 z-50 bg-black flex items-center justify-center`} data-id="element-82">
        <video ref={videoRef} className="w-full h-full object-contain bg-black" controls controlsList="nodownload noplaybackrate" disablePictureInPicture onContextMenu={e => e.preventDefault()} playsInline preload="auto" poster="https://images.unsplash.com/photo-1478720568477-152d9b164e63?q=80&w=2500&auto=format&fit=crop" data-id="element-83">
          <source src="https://cdn.shopify.com/videos/c/o/v/46f24469e7894a26a2d058257b630f2c.mp4" type="video/mp4" data-id="element-84" />
        </video>
      </div>

      {/* Navigation Bar */}
      <nav className="w-full p-6 flex justify-between items-center z-30" data-id="element-85">
        <button onClick={onBack} className="flex items-center text-[var(--text-secondary)] hover:text-white transition-colors text-sm tracking-widest uppercase group" data-id="element-86">
          <ChevronLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-87" />
          Volver
        </button>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 md:px-12 z-20" data-id="element-88">
        <div className="max-w-4xl w-full mx-auto" data-id="element-89">
          <motion.div variants={itemVariants} className="mb-4" data-id="element-90">
            <span className="text-[var(--text-secondary)] uppercase tracking-[0.2em] text-xs md:text-sm font-medium border-l-2 border-[var(--accent)] pl-4" data-id="element-91">
              Temporada 1 — Caso del Auto Blanco
            </span>
          </motion.div>

          <motion.h2 variants={itemVariants} className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-[var(--text-primary)] mb-8 leading-tight" data-id="element-92">
            EPISODIO 1 — <br className="md:hidden" data-id="element-93" />
            <span className="text-white" data-id="element-94">DESAPARECIDA</span>
          </motion.h2>

          <motion.div variants={itemVariants} className="w-24 h-1 bg-[var(--accent)] mb-10" data-id="element-95" />

          <motion.div variants={itemVariants} className="space-y-4 mb-12 max-w-2xl" data-id="element-96">
            <p className="text-lg md:text-xl font-light text-[var(--text-primary)] leading-relaxed" data-id="element-97">
              Carmen García desaparece.
            </p>
            <p className="text-lg md:text-xl font-light text-[var(--text-primary)] leading-relaxed" data-id="element-98">
              Cinco días después, su cuerpo aparece en Guadarrama.
            </p>
            <p className="text-lg md:text-xl font-light text-[var(--text-primary)] leading-relaxed" data-id="element-99">
              Tres personas están en el centro de la historia.
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-start sm:items-center gap-6" data-id="element-100">
            <button onClick={handlePlayFullscreen} className="flex items-center justify-center px-8 py-4 bg-[var(--accent)] text-white font-medium tracking-widest uppercase hover:bg-[var(--accent-hover)] transition-all duration-300 shadow-[0_0_30px_rgba(139,26,26,0.3)] hover:shadow-[0_0_50px_rgba(139,26,26,0.5)] hover:scale-105" data-id="element-101">
              <Play className="w-5 h-5 mr-3 fill-current" data-id="element-102" />
              {hasWatched ? 'Volver a Ver' : 'Ver Episodio 1'}
            </button>

            <button onClick={() => setIsListOpen(true)} className="flex items-center text-[var(--text-primary)] hover:text-[var(--accent)] transition-colors text-sm tracking-widest uppercase group py-2" data-id="element-103">
              <List className="w-5 h-5 mr-3" data-id="element-104" />
              <span className="group-hover:underline underline-offset-4 decoration-1" data-id="element-105">
                Ver lista de episodios
              </span>
            </button>
          </motion.div>

          {/* Episode info revealed after watching */}
          <AnimatePresence data-id="element-106">
            {hasWatched && <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="mt-16 space-y-12 max-w-3xl" data-id="element-107">
                {/* Divider */}
                <div className="w-full h-px bg-white/10" data-id="element-108" />

                {/* Description */}
                <div className="space-y-6" data-id="element-109">
                  <h3 className="text-xl font-serif text-[var(--text-primary)] mb-4" data-id="element-110">
                    En este episodio conocerás:
                  </h3>
                  <ul className="space-y-3 text-[var(--text-secondary)] font-light" data-id="element-111">
                    <li className="flex items-center" data-id="element-112">
                      <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3" data-id="element-113" />
                      La última noche de Carmen
                    </li>
                    <li className="flex items-center" data-id="element-114">
                      <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3" data-id="element-115" />
                      La llamada misteriosa
                    </li>
                    <li className="flex items-center" data-id="element-116">
                      <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3" data-id="element-117" />
                      Los tres principales sospechosos
                    </li>
                  </ul>
                </div>

                {/* Warning Box */}
                <motion.div className="bg-[var(--bg-card)] border-l-4 border-[var(--accent)] p-6 md:p-8 relative overflow-hidden" initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3
            }} data-id="element-118">
                  <div className="absolute top-0 right-0 p-4 opacity-5" data-id="element-119">
                    <AlertTriangle className="w-24 h-24" data-id="element-120" />
                  </div>
                  <div className="relative z-10" data-id="element-121">
                    <div className="flex items-center mb-4 text-[var(--accent)]" data-id="element-122">
                      <AlertTriangle className="w-5 h-5 mr-2" data-id="element-123" />
                      <span className="font-bold tracking-widest uppercase text-sm" data-id="element-124">
                        Importante
                      </span>
                    </div>
                    <p className="text-[var(--text-primary)] text-lg mb-2" data-id="element-125">
                      Este episodio incluye una pausa obligatoria para que
                      investiguen.
                    </p>
                    <p className="text-[var(--text-secondary)]" data-id="element-126">
                      No usen el celular.
                    </p>
                  </div>
                </motion.div>

                {/* Next Episode Button / Countdown */}
                <motion.div className="flex flex-col items-center pt-4 pb-12 gap-6" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.6
            }} data-id="element-127">
                  {isUnlocked ? <button onClick={onNextEpisode} className="px-8 py-4 border border-white/10 text-[var(--text-secondary)] uppercase tracking-widest text-sm hover:bg-white/5 hover:text-white hover:border-white/30 transition-all duration-300" data-id="element-128">
                      Continuar al Episodio 2
                    </button> : <div className="flex flex-col items-center gap-5" data-id="element-129">
                      <div className="flex items-center gap-2 text-[var(--text-secondary)] text-sm uppercase tracking-widest" data-id="element-130">
                        <Lock className="w-4 h-4 text-[var(--accent)]" data-id="element-131" />
                        <span data-id="element-132">Episodio 2 se desbloquea en</span>
                      </div>

                      {timeLeft && <div className="flex items-center gap-3" data-id="element-133">
                          <div className="flex flex-col items-center" data-id="element-134">
                            <span className="text-3xl md:text-4xl font-serif font-bold text-[var(--text-primary)] tabular-nums w-16 text-center" data-id="element-135">
                              {String(timeLeft.h).padStart(2, '0')}
                            </span>
                            <span className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)] mt-1" data-id="element-136">
                              Horas
                            </span>
                          </div>
                          <span className="text-2xl text-[var(--accent)] font-light -mt-4" data-id="element-137">
                            :
                          </span>
                          <div className="flex flex-col items-center" data-id="element-138">
                            <span className="text-3xl md:text-4xl font-serif font-bold text-[var(--text-primary)] tabular-nums w-16 text-center" data-id="element-139">
                              {String(timeLeft.m).padStart(2, '0')}
                            </span>
                            <span className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)] mt-1" data-id="element-140">
                              Min
                            </span>
                          </div>
                          <span className="text-2xl text-[var(--accent)] font-light -mt-4" data-id="element-141">
                            :
                          </span>
                          <div className="flex flex-col items-center" data-id="element-142">
                            <span className="text-3xl md:text-4xl font-serif font-bold text-[var(--text-primary)] tabular-nums w-16 text-center" data-id="element-143">
                              {String(timeLeft.s).padStart(2, '0')}
                            </span>
                            <span className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)] mt-1" data-id="element-144">
                              Seg
                            </span>
                          </div>
                        </div>}

                      <div className="flex items-center gap-2 text-[var(--text-secondary)]/60 text-xs mt-1" data-id="element-145">
                        <Clock className="w-3 h-3" data-id="element-146" />
                        <span data-id="element-147">Hoy a las 23:00</span>
                      </div>
                    </div>}
                </motion.div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      <EpisodeList isOpen={isListOpen} onClose={() => setIsListOpen(false)} onSelectEpisode={id => {
      setIsListOpen(false);
      if (id === 1) handlePlayFullscreen();
    }} data-id="element-148" />
    </motion.div>;
}